import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, MapPin, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const reference = searchParams.get('reference') || searchParams.get('trxref');
    
    if (reference) {
      verifyPayment(reference);
    } else {
      setStatus('failed');
    }
  }, [searchParams]);

  const verifyPayment = async (reference) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/verify/${reference}`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        setPaymentData(data);
        setStatus('success');
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      setStatus('failed');
    }
  };

  const copyReference = () => {
    if (paymentData?.booking_reference) {
      navigator.clipboard.writeText(paymentData.booking_reference);
      setCopied(true);
      toast.success('Reference copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <main data-testid="payment-callback-page" className="bg-white min-h-screen pt-24 pb-16">
      <div className="max-w-[600px] mx-auto px-6">
        {status === 'verifying' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Loader2 className="w-12 h-12 text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-semibold text-[#1d1d1f] mb-2">
              Verifying payment...
            </h1>
            <p className="text-sm text-[#86868b]">
              Please wait while we confirm your payment.
            </p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
            data-testid="payment-success"
          >
            <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-semibold text-[#1d1d1f] mb-3">
              Payment successful!
            </h1>
            <p className="text-base text-[#86868b] mb-8 max-w-md mx-auto">
              Your booking has been confirmed. We will contact you shortly to finalize the details.
            </p>
            
            {paymentData && (
              <div className="bg-[#f5f5f7] rounded-2xl p-6 mb-8 text-left">
                {/* Booking Reference - Prominent */}
                {paymentData.booking_reference && (
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <p className="text-xs text-[#86868b] uppercase tracking-wide mb-1">Your Tracking Reference</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-[#C41E3A]" data-testid="booking-reference">
                        {paymentData.booking_reference}
                      </span>
                      <button 
                        onClick={copyReference}
                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                        data-testid="copy-reference-btn"
                      >
                        {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-gray-500" />}
                      </button>
                    </div>
                    <p className="text-xs text-[#86868b] mt-1">Use this to track your service status</p>
                  </div>
                )}
                
                <h3 className="text-sm font-medium text-[#1d1d1f] mb-4">Payment Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#86868b]">Amount</span>
                    <span className="text-[#1d1d1f] font-medium">
                      R{paymentData.amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#86868b]">Status</span>
                    <span className="text-green-600 font-medium">Paid</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {paymentData?.booking_reference && (
                <Link
                  to={`/track/${paymentData.booking_reference}`}
                  className="apple-btn apple-btn-primary btn-press flex items-center gap-2 justify-center"
                  data-testid="track-service-btn"
                >
                  <MapPin className="w-4 h-4" />
                  Track Your Service
                </Link>
              )}
              <Link
                to="/"
                className="apple-btn apple-btn-secondary btn-press"
                data-testid="return-home-btn"
              >
                Return home
              </Link>
            </div>
          </motion.div>
        )}

        {status === 'failed' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
            data-testid="payment-failed"
          >
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-3xl font-semibold text-[#1d1d1f] mb-3">
              Payment unsuccessful
            </h1>
            <p className="text-base text-[#86868b] mb-8 max-w-md mx-auto">
              We could not verify your payment. Please try again or contact us for assistance.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking"
                className="apple-btn apple-btn-primary btn-press"
                data-testid="try-again-btn"
              >
                Try again
              </Link>
              <Link
                to="/contact"
                className="apple-btn apple-btn-secondary btn-press"
              >
                Contact support
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}
